import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  Archive,
  ArchiveRestore,
  Building2,
  CalendarClock,
  FolderOpen,
  Globe,
  Pencil,
  RefreshCw,
  Server,
  ShieldCheck,
  Wrench,
} from 'lucide-react';
import {
  DEFAULT_REMIND_DAYS,
  RENEWAL_KINDS,
  RENEWAL_KIND_LABEL,
  addMonths,
  businessDate,
  describeDaysLeft,
  formatInr,
  paise,
  renewalUrgency,
  type RenewalKind,
  type RenewalUrgency,
} from '@bits/domain';
import type { RenewalInput, RenewalSummary } from '@bits/shared/zod';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Field } from '@/components/ui/field';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export const KIND_ICON: Record<RenewalKind, React.ComponentType<{ className?: string }>> = {
  domain: Globe,
  hosting: Server,
  amc: Wrench,
  ssl: ShieldCheck,
  other: RefreshCw,
};

export const CYCLE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Not set' },
  { value: '1', label: 'Monthly' },
  { value: '3', label: 'Quarterly' },
  { value: '6', label: 'Half-yearly' },
  { value: '12', label: 'Yearly' },
  { value: '24', label: 'Every 2 years' },
  { value: '36', label: 'Every 3 years' },
];

const URGENCY_CLASS: Record<RenewalUrgency, string> = {
  expired: 'bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))]',
  today: 'bg-[hsl(var(--destructive))] text-[hsl(var(--destructive-foreground))]',
  critical: 'bg-amber-500 text-white',
  soon: 'bg-amber-100 text-amber-800',
  later: 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]',
};

export function formatRenewalDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

export function cycleLabel(months: number | null): string {
  if (months === null) return 'Not set';
  return CYCLE_OPTIONS.find((c) => c.value === String(months))?.label ?? `Every ${months} months`;
}

export function UrgencyPill({ daysLeft }: { daysLeft: number | null }): JSX.Element {
  if (daysLeft === null) return <Badge variant="muted">No expiry set</Badge>;
  const urgency = renewalUrgency(daysLeft);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium',
        URGENCY_CLASS[urgency],
      )}
    >
      <CalendarClock className="size-3.5" aria-hidden />
      {describeDaysLeft(daysLeft)}
    </span>
  );
}

export function RenewalCard({
  renewal: r,
  canEdit,
  busy,
  showProject,
  highlighted,
  onEdit,
  onRenewed,
  onArchive,
  onRestore,
}: {
  renewal: RenewalSummary;
  canEdit: boolean;
  busy: boolean;
  showProject?: boolean;
  highlighted?: boolean;
  onEdit: () => void;
  onRenewed?: () => void;
  onArchive?: () => void;
  onRestore?: () => void;
}): JSX.Element {
  const Icon = KIND_ICON[r.kind];
  const archived = r.archivedAt !== null;
  const canRenew = Boolean(onRenewed && r.expiresOn && r.cycleMonths);
  return (
    <Card
      id={r.id}
      className={cn(
        'h-full scroll-mt-24 transition-shadow',
        archived && 'opacity-70',
        highlighted &&
          'ring-2 ring-[hsl(var(--brand))] ring-offset-2 ring-offset-[hsl(var(--background))]',
      )}
    >
      <CardHeader className="flex-row items-start justify-between gap-3 pb-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">
            <Icon className="size-5" aria-hidden />
          </span>
          <div className="flex min-w-0 flex-col gap-0.5">
            <CardTitle className="truncate text-base">{r.label}</CardTitle>
            <CardDescription>
              {RENEWAL_KIND_LABEL[r.kind]}
              {r.provider ? ` · ${r.provider}` : ''}
            </CardDescription>
          </div>
        </div>
        {archived ? <Badge variant="muted">Archived</Badge> : <UrgencyPill daysLeft={r.daysLeft} />}
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-0">
        {showProject ? (
          <div className="flex flex-wrap items-center gap-2">
            {r.project ? (
              <Link
                to="/projects/$projectId"
                params={{ projectId: r.project.id }}
                className="inline-flex max-w-full items-center gap-2 rounded-lg bg-[hsl(var(--muted))] px-2.5 py-1 text-xs font-medium text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))]"
              >
                <FolderOpen className="size-3.5 shrink-0" aria-hidden />
                <span className="font-mono">{r.project.code}</span>
                <span className="truncate font-normal text-[hsl(var(--muted-foreground))]">
                  {r.project.name}
                  {r.project.status === 'closed' ? ' · closed' : ''}
                </span>
              </Link>
            ) : null}
            {r.client ? (
              <span
                className="inline-flex max-w-full items-center gap-2 rounded-lg bg-[hsl(var(--accent))] px-2.5 py-1 text-xs font-medium text-[hsl(var(--accent-foreground))]"
                title={r.projectId && !r.clientId ? 'Client of the linked project' : 'Client'}
              >
                <Building2 className="size-3.5 shrink-0" aria-hidden />
                <span className="truncate">For {r.client.displayName}</span>
              </span>
            ) : null}
            {!r.project && !r.client ? (
              <span className="inline-flex items-center gap-2 rounded-lg border border-dashed border-[hsl(var(--border))] px-2.5 py-1 text-xs text-[hsl(var(--muted-foreground))]">
                <Building2 className="size-3.5" aria-hidden />
                Company-level
              </span>
            ) : null}
          </div>
        ) : null}
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-[hsl(var(--muted-foreground))]">Expires on</dt>
          <dd className="font-medium">{r.expiresOn ? formatRenewalDate(r.expiresOn) : '—'}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">Renewal cycle</dt>
          <dd>{cycleLabel(r.cycleMonths)}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">Amount</dt>
          <dd>{r.amountPaise === null ? '—' : formatInr(paise(BigInt(r.amountPaise)))}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">Remind</dt>
          <dd>{r.remindDays.length ? `${r.remindDays.join(', ')} days before` : 'Off'}</dd>
        </dl>
        {r.notes ? (
          <p className="whitespace-pre-wrap text-sm text-[hsl(var(--muted-foreground))]">
            {r.notes}
          </p>
        ) : null}
        {canEdit ? (
          <div className="flex flex-wrap gap-2 border-t border-[hsl(var(--border))] pt-3">
            <Button size="sm" variant="outline" onClick={onEdit} disabled={busy}>
              <Pencil />
              Edit
            </Button>
            {canRenew && r.expiresOn && r.cycleMonths ? (
              <Button
                size="sm"
                variant="outline"
                onClick={onRenewed}
                disabled={busy}
                title={`Moves the expiry to ${formatRenewalDate(addMonths(businessDate(r.expiresOn), r.cycleMonths))}`}
              >
                <RefreshCw />
                Renewed for next cycle
              </Button>
            ) : null}
            {onArchive ? (
              <Button size="sm" variant="ghost" onClick={onArchive} disabled={busy}>
                <Archive />
                Archive
              </Button>
            ) : null}
            {onRestore ? (
              <Button size="sm" variant="ghost" onClick={onRestore} disabled={busy}>
                <ArchiveRestore />
                Restore
              </Button>
            ) : null}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

type FormValues = {
  projectId: string;
  clientId: string;
  kind: RenewalKind;
  label: string;
  provider: string;
  expiresOn: string;
  cycleMonths: string;
  amountRupees: string;
  remindDays: string;
  notes: string;
};

function emptyForm(projectId: string): FormValues {
  return {
    projectId,
    clientId: '',
    kind: 'domain',
    label: '',
    provider: '',
    expiresOn: '',
    cycleMonths: '12',
    amountRupees: '',
    remindDays: DEFAULT_REMIND_DAYS.join(', '),
    notes: '',
  };
}

function formFrom(r: RenewalSummary): FormValues {
  return {
    projectId: r.projectId ?? '',
    clientId: r.clientId ?? '',
    kind: r.kind,
    label: r.label,
    provider: r.provider ?? '',
    expiresOn: r.expiresOn ?? '',
    cycleMonths: r.cycleMonths === null ? '' : String(r.cycleMonths),
    amountRupees: r.amountPaise === null ? '' : (r.amountPaise / 100).toFixed(2),
    remindDays: r.remindDays.join(', '),
    notes: r.notes ?? '',
  };
}

function parseRemindDays(text: string): number[] | null {
  const parts = text
    .split(/[,\s]+/)
    .map((p) => p.trim())
    .filter(Boolean);
  const nums = parts.map(Number);
  if (nums.some((n) => !Number.isInteger(n) || n < 0 || n > 730)) return null;
  return nums;
}

function toInput(v: FormValues): RenewalInput {
  const rupees = v.amountRupees.trim() === '' ? null : Number(v.amountRupees);
  if (rupees !== null && (!Number.isFinite(rupees) || rupees < 0)) {
    throw new Error('Amount must be a non-negative number');
  }
  const remindDays = parseRemindDays(v.remindDays);
  if (remindDays === null) {
    throw new Error('Reminder days must be whole numbers between 0 and 730');
  }
  return {
    projectId: v.projectId || null,
    clientId: v.clientId || null,
    kind: v.kind,
    label: v.label.trim(),
    provider: v.provider.trim() || null,
    expiresOn: v.expiresOn || null,
    amountPaise: rupees === null ? null : Math.round(rupees * 100),
    cycleMonths: v.cycleMonths ? Number(v.cycleMonths) : null,
    remindDays,
    notes: v.notes.trim() || null,
  };
}

export type RenewalDialogState =
  { mode: 'add'; projectId?: string } | { mode: 'edit'; renewal: RenewalSummary } | null;

const LABEL_HINT: Record<RenewalKind, string> = {
  domain: 'The domain name, e.g. acme.in',
  hosting: 'The plan or server, e.g. Hostinger Cloud Startup',
  ssl: 'Certificate name or the domain it covers',
  amc: 'What the AMC covers, e.g. Annual maintenance 2026-27',
  other: 'What is being renewed',
};

export function RenewalDialog({
  state,
  lockProject = false,
  onClose,
  onSaved,
}: {
  state: RenewalDialogState;
  lockProject?: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}): JSX.Element {
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const form = useForm<FormValues>({ defaultValues: emptyForm('') });
  const open = state !== null;

  const projectsQuery = useQuery({
    queryKey: ['projects', 'for-renewals'],
    queryFn: () => api.listProjects({}),
    enabled: open && !lockProject,
  });
  const clientsQuery = useQuery({
    queryKey: ['clients-for-renewals'],
    queryFn: () => api.listClients({ status: 'active', page: 1, pageSize: 100 }),
    enabled: open && !lockProject,
  });

  React.useEffect(() => {
    if (!state) return;
    form.reset(state.mode === 'edit' ? formFrom(state.renewal) : emptyForm(state.projectId ?? ''));
    setErrorMessage(null);
  }, [state, form]);

  const mutation = useMutation({
    mutationFn: async (v: FormValues) => {
      const body = toInput(v);
      if (state?.mode === 'edit') {
        await api.updateRenewal(state.renewal.id, body);
        return;
      }
      await api.createRenewal(body);
    },
    onSuccess: onSaved,
    onError: (err) => setErrorMessage(toMessage(err)),
  });

  const kind = form.watch('kind');
  const projects = projectsQuery.data ?? [];
  const clients = clientsQuery.data?.items ?? [];
  const selectedProjectId = form.watch('projectId');

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{state?.mode === 'edit' ? 'Edit renewal' : 'Add renewal'}</DialogTitle>
          <DialogDescription>
            Only the label is required. Add the expiry date whenever you have it and reminders start
            from there.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          className="flex flex-col gap-3"
        >
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type" required>
              <Select {...form.register('kind')}>
                {RENEWAL_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {RENEWAL_KIND_LABEL[k]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Provider" hint="Registrar, host or vendor">
              <Input {...form.register('provider')} placeholder="e.g. Hostinger" />
            </Field>
          </div>
          <Field
            label="Label"
            required
            hint={LABEL_HINT[kind]}
            error={form.formState.errors.label?.message}
          >
            <Input {...form.register('label', { required: 'Enter a label' })} />
          </Field>
          {lockProject ? null : (
            <Field
              label="Linked project"
              hint="Optional. Closed projects can still be linked so the renewal keeps its history."
            >
              <Select {...form.register('projectId')} disabled={projectsQuery.isPending}>
                <option value="">— None, company-level —</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code} · {p.name}
                    {p.status === 'closed' ? ' (closed)' : ''}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {lockProject ? null : selectedProjectId ? (
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              The client is taken from the linked project.
            </p>
          ) : (
            <Field
              label="For client"
              hint="Optional. Says whose renewal this is when there is no project behind it."
            >
              <Select {...form.register('clientId')} disabled={clientsQuery.isPending}>
                <option value="">— None, company-level —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Expires on">
              <Input type="date" {...form.register('expiresOn')} />
            </Field>
            <Field label="Renewal cycle" hint="Used by “Renewed for next cycle”">
              <Select {...form.register('cycleMonths')}>
                {CYCLE_OPTIONS.map((c) => (
                  <option key={c.value || 'none'} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Amount (INR)" hint="Per cycle, optional">
              <Input type="number" step="0.01" min="0" {...form.register('amountRupees')} />
            </Field>
            <Field label="Remind (days before)" hint="Comma-separated. 0 means on the day">
              <Input {...form.register('remindDays')} />
            </Field>
          </div>
          <Field label="Notes">
            <Textarea
              rows={3}
              {...form.register('notes')}
              placeholder="Login location, auto-renew status, who pays…"
            />
          </Field>
          {errorMessage ? (
            <Alert variant="destructive">
              <AlertTitle>Save failed</AlertTitle>
              <AlertDescription>{errorMessage}</AlertDescription>
            </Alert>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending
                ? 'Saving…'
                : state?.mode === 'edit'
                  ? 'Save changes'
                  : 'Add renewal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function useRenewalQuickActions(invalidate: () => Promise<void>) {
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const mutation = useMutation({
    mutationFn: (args: { id: string; body: Parameters<typeof api.updateRenewal>[1] }) =>
      api.updateRenewal(args.id, args.body),
    onSuccess: async () => {
      setErrorMessage(null);
      await invalidate();
    },
    onError: (err) => setErrorMessage(toMessage(err)),
  });
  return {
    busy: mutation.isPending,
    errorMessage,
    clearError: () => setErrorMessage(null),
    renewed: (r: RenewalSummary) => {
      if (!r.expiresOn || !r.cycleMonths) return;
      mutation.mutate({
        id: r.id,
        body: { expiresOn: addMonths(businessDate(r.expiresOn), r.cycleMonths) },
      });
    },
    archive: (r: RenewalSummary) => mutation.mutate({ id: r.id, body: { archived: true } }),
    restore: (r: RenewalSummary) => mutation.mutate({ id: r.id, body: { archived: false } }),
  };
}
